import { ArrowLeft, Calendar } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";

const MicronutrientRiskDetail = () => {
  const navigate = useNavigate();

  const dailyData = Array.from({ length: 7 }, (_, i) => ({
    meal: `Meal ${i + 1}`,
    score: Math.floor(Math.random() * 30 + 60),
  }));

  const weeklyData = [
    { day: "Mon", score: 72 },
    { day: "Tue", score: 68 },
    { day: "Wed", score: 85 },
    { day: "Thu", score: 78 },
    { day: "Fri", score: 82 },
    { day: "Sat", score: 75 },
    { day: "Sun", score: 80 },
  ];

  const monthlyData = [
    { week: "Week 1", score: 72 },
    { week: "Week 2", score: 75 },
    { week: "Week 3", score: 80 },
    { week: "Week 4", score: 78 },
  ];

  return (
    <div className="min-h-screen bg-background pb-16">
      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-title2 font-bold text-foreground">
            Micronutrient Risk
          </h1>
          <Button
            variant="ghost"
            size="icon"
            className="rounded-xl"
            onClick={() => {
              // TODO: Implement calendar functionality
            }}
          >
            <Calendar className="h-5 w-5" />
          </Button>
        </div>

        {/* Today's Stats */}
        <Card className="from-warning/10 to-warning/5 rounded-2xl border-0 bg-gradient-to-br p-6 shadow-sm">
          <div className="text-center">
            <p className="mb-2 text-subhead text-muted-foreground">
              Today's Diversity Score
            </p>
            <div className="mb-2 text-5xl font-bold text-foreground">78%</div>
            <p className="text-subhead text-muted-foreground">Good Coverage</p>
            <div className="mt-4 flex items-center justify-center gap-2">
              <span className="text-caption1 text-muted-foreground">Poor</span>
              <div className="via-warning to-success h-2 w-48 rounded-full bg-gradient-to-r from-destructive">
                <div className="relative h-full w-full">
                  <div className="absolute left-[78%] top-1/2 h-3 w-3 -translate-y-1/2 rounded-full border-2 border-background bg-foreground" />
                </div>
              </div>
              <span className="text-caption1 text-muted-foreground">Excellent</span>
            </div>
          </div>
        </Card>

        {/* What is Micronutrient Risk */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">
            What is Micronutrient Risk?
          </h3>
          <p className="text-subhead leading-relaxed text-muted-foreground">
            Micronutrient diversity measures how well your diet covers essential
            vitamins and minerals. A diverse intake of nutrients reduces the
            risk of deficiencies and supports optimal body function. This score
            evaluates the variety of vitamins, minerals, and phytonutrients in
            your meals.
          </p>
          <div className="mt-4 space-y-2">
            <div className="flex justify-between text-subhead">
              <span className="text-muted-foreground">Excellent Coverage</span>
              <span className="text-success font-semibold">90-100%</span>
            </div>
            <div className="flex justify-between text-subhead">
              <span className="text-muted-foreground">Good Coverage</span>
              <span className="font-semibold text-primary">70-89%</span>
            </div>
            <div className="flex justify-between text-subhead">
              <span className="text-muted-foreground">Moderate Risk</span>
              <span className="text-warning font-semibold">50-69%</span>
            </div>
            <div className="flex justify-between text-subhead">
              <span className="text-muted-foreground">High Risk</span>
              <span className="font-semibold text-destructive">&lt;50%</span>
            </div>
          </div>
        </Card>

        {/* Charts */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <Tabs defaultValue="daily" className="w-full">
            <TabsList className="mb-4 grid w-full grid-cols-3">
              <TabsTrigger value="daily">Daily</TabsTrigger>
              <TabsTrigger value="weekly">Weekly</TabsTrigger>
              <TabsTrigger value="monthly">Monthly</TabsTrigger>
            </TabsList>

            <TabsContent value="daily" className="space-y-4">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dailyData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="hsl(var(--border))"
                    />
                    <XAxis
                      dataKey="meal"
                      stroke="hsl(var(--muted-foreground))"
                      style={{ fontSize: "var(--font-size-caption2)" }}
                    />
                    <YAxis
                      stroke="hsl(var(--muted-foreground))"
                      style={{ fontSize: "var(--font-size-caption2)" }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                      }}
                    />
                    <Bar
                      dataKey="score"
                      fill="hsl(var(--primary))"
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>

            <TabsContent value="weekly" className="space-y-4">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={weeklyData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="hsl(var(--border))"
                    />
                    <XAxis
                      dataKey="day"
                      stroke="hsl(var(--muted-foreground))"
                      style={{ fontSize: "var(--font-size-caption2)" }}
                    />
                    <YAxis
                      stroke="hsl(var(--muted-foreground))"
                      style={{ fontSize: "var(--font-size-caption2)" }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                      }}
                    />
                    <Bar
                      dataKey="score"
                      fill="hsl(var(--primary))"
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>

            <TabsContent value="monthly" className="space-y-4">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="hsl(var(--border))"
                    />
                    <XAxis
                      dataKey="week"
                      stroke="hsl(var(--muted-foreground))"
                      style={{ fontSize: "var(--font-size-caption2)" }}
                    />
                    <YAxis
                      stroke="hsl(var(--muted-foreground))"
                      style={{ fontSize: "var(--font-size-caption2)" }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                      }}
                    />
                    <Bar
                      dataKey="score"
                      fill="hsl(var(--primary))"
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>
          </Tabs>
        </Card>

        {/* Key Insights */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">Key Insights</h3>
          <div className="space-y-3">
            <div className="flex gap-3">
              <div className="bg-success mt-2 h-2 w-2 rounded-full" />
              <div className="flex-1">
                <p className="text-subhead text-muted-foreground">
                  Your vitamin A intake is excellent from leafy greens and
                  carrots
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="bg-warning mt-2 h-2 w-2 rounded-full" />
              <div className="flex-1">
                <p className="text-subhead text-muted-foreground">
                  Consider adding more omega-3 rich foods like fatty fish or
                  chia seeds
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="mt-2 h-2 w-2 rounded-full bg-primary" />
              <div className="flex-1">
                <p className="text-subhead text-muted-foreground">
                  Your mineral diversity has improved by 15% this week
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default MicronutrientRiskDetail;
